import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Button,
  Tooltip,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

const ProductCodeSnippetHelper = () => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleCopy = (code, index) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const codeSnippets = {
    setup: {
      title: '1. Setup & Installation',
      description: 'Install and configure the widget library in your project',
      snippets: [
        {
          label: 'Step 1: Install Package',
          language: 'bash',
          code: `npm install @datum-ai-labs/react-widgets`
        },
        {
          label: 'Step 2: Setup Configuration',
          language: 'tsx',
          code: `import { setupDatumart } from '@datum-ai-labs/react-widgets';

// Add this in your root layout or _app.tsx
setupDatumart({
  apiUrl: process.env.NEXT_PUBLIC_API_URL,
  imageSourcePath: process.env.NEXT_PUBLIC_IMAGE_SOURCE_PATH,
});`
        }
      ]
    },
    productWidget: {
      title: '2. Product Widget (UI Mode)',
      description: 'Display products for any industry (Restaurant/Food, Retail/E-Commerce, SaaS, Digital Services) with search, categories & cart/subscribe actions',
      snippets: [
        {
          label: 'Auto-Detect Mode (General Products & Services)',
          language: 'tsx',
          code: `import { ProductWidget } from '@datum-ai-labs/react-widgets';

// Auto-detects product type (Restaurant, SaaS, or E-Commerce)
<ProductWidget 
  category="all" 
  limit={12} 
  theme="dark" 
  mode="auto"
/>`
        },
        {
          label: 'Restaurant & Food Menu Mode (mode="restaurant")',
          language: 'tsx',
          code: `import { ProductWidget } from '@datum-ai-labs/react-widgets';

// Out-of-the-box UI for Restaurant & Food items with Cart controls (+ / -), prices & Veg/Non-Veg badges
<ProductWidget 
  category="all" 
  limit={12} 
  theme="light" 
  mode="restaurant"
  onAddToCart={(product) => console.log('Added to cart:', product)}
/>`
        },
        {
          label: 'Filter by Specific Category',
          language: 'tsx',
          code: `import { ProductWidget } from '@datum-ai-labs/react-widgets';

<ProductWidget 
  category={selectedCategory} 
  limit={6} 
  theme="light"
/>`
        },
        {
          label: 'With Custom Styling & Callbacks',
          language: 'tsx',
          code: `import { ProductWidget } from '@datum-ai-labs/react-widgets';

<ProductWidget 
  category="all"
  limit={12}
  theme="light"
  styles={{
    backgroundColor: "transparent",
    cardBg: "#FFFFFF",
    headingColor: "#111827",
    textColor: "#475569",
    accentColor: "#ED6900",
    buttonColor: "#FF8D4A",
    buttonHoverColor: "#ED6900",
    buttonTextColor: "#130600",
    borderColor: "#E5E7EB",
    badgeBg: "#FFEDE6",
    badgeTextColor: "#130600",
    featureCheckColor: "#ED6900",
    currencySymbol: "$", // Optional: Override store settings currency symbol
  }}
  onProductClick={(product) => console.log('Selected:', product)}
  onSubscribe={(product) => window.location.href = '/onboarding'}
  onAddToCart={(product) => console.log('Added to cart:', product)}
/>`
        }
      ]
    },
    headless: {
      title: '3. Headless Mode (useProducts)',
      description: 'Use the useProducts hook to fetch normalized data and build 100% custom product UI designs',
      snippets: [
        {
          label: 'useProducts Hook (Custom Product Grid)',
          language: 'tsx',
          code: `import { useProducts } from '@datum-ai-labs/react-widgets';
import { CheckCircle } from 'lucide-react';

const CustomMarketplace = () => {
  // Returns normalized products & dynamic categories cleanly in 1 line
  const { products, categories, isLoading, error } = useProducts();

  if (isLoading) return <p>Loading Marketplace...</p>;
  if (error) return <p>Error loading products: {error}</p>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {products.map((product) => (
        <div key={product.id} className="p-6 bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl">
          <img src={product.image} alt={product.title} className="w-full h-48 object-cover rounded-xl" />
          <h3 className="text-xl font-bold text-[#111827] mt-4">{product.title}</h3>
          <p className="text-[#475569] text-sm mt-2">{product.description}</p>
          
          {/* Feature Bullet Points */}
          <div className="mt-4 space-y-2">
            {product.features?.map((feat) => (
              <div key={feat} className="flex items-center gap-2 text-sm text-[#475569]">
                <CheckCircle size={16} className="text-[#ED6900]" />
                <span>{feat}</span>
              </div>
            ))}
          </div>

          <button 
            onClick={() => {
              if (product.buttonLink) window.open(product.buttonLink, '_blank');
              else window.location.href = '/onboarding';
            }}
            className="w-full mt-6 py-3 bg-[#FF8D4A] hover:bg-[#ED6900] text-[#130600] rounded-xl font-bold transition"
          >
            {product.buttonText || 'Subscribe Now'} →
          </button>
        </div>
      ))}
    </div>
  );
};`
        }
      ]
    },
    tracking: {
      title: '4. Analytics & SEO Tracking',
      description: 'Standalone analytics functions for GTM, GA4, Meta Pixel, and custom event tracking',
      snippets: [
        {
          label: 'Product Analytics & Conversions',
          language: 'tsx',
          code: `import { 
  trackProductView, 
  trackProductCardClick, 
  trackProductSubscribe, 
  trackProductSearch 
} from '@datum-ai-labs/react-widgets';

// 1. Track Product View / Modal Open (Dynamic Product Data)
trackProductView({
  productId: product.id,
  productTitle: product.title,
  category: product.category,
  price: product.price,
});

// 2. Track Card Click (Dynamic Event Handler)
trackProductCardClick({
  productId: product.id,
  productTitle: product.title,
  category: product.category,
});

// 3. Track Subscription Intent (GA4 begin_checkout)
trackProductSubscribe({
  productId: product.id,
  productTitle: product.title,
  category: product.category,
});

// 4. Track Product Search
trackProductSearch(searchQuery, searchResults.length);`
        }
      ]
    }
  };

  const tabData = Object.keys(codeSnippets);

  return (
    <>
      <Tooltip title="Product Code Helper" arrow>
        <Button
          onClick={handleOpen}
          sx={{
            borderRadius: '12px',
            color: '#0d3559',
            minWidth: '40px',
            width: '40px',
            height: '40px',
            p: 0,
            '&:hover': {
              bgcolor: 'rgba(13, 53, 89, 0.08)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <CodeIcon sx={{ fontSize: 22 }} />
        </Button>
      </Tooltip>

      <Modal
        open={open}
        onClose={handleClose}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            width: '90%',
            maxWidth: 1100,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: '16px',
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: '1px solid #F1F3F9',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: '#F8F9FE'
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1F2937', mb: 0.5 }}>
                📦 Product Widget Code Helper
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                Copy-paste ready product code snippets for @datum-ai-labs/react-widgets
              </Typography>
            </Box>
            <IconButton onClick={handleClose} sx={{ color: '#525F7F' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  minHeight: '48px'
                },
                '& .Mui-selected': {
                  color: '#0d3559 !important'
                }
              }}
            >
              {tabData.map((key) => (
                <Tab key={key} label={codeSnippets[key].title} />
              ))}
            </Tabs>
          </Box>

          {/* Content */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 4 }}>
            {tabData.map((key, tabIndex) => (
              <Box key={key} hidden={activeTab !== tabIndex}>
                <Typography variant="body1" sx={{ color: '#6B7280', mb: 3 }}>
                  {codeSnippets[key].description}
                </Typography>

                {codeSnippets[key].snippets.map((snippet, index) => (
                  <Box key={`${key}-${snippet.label}`} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: '#1F2937' }}
                        >
                          {snippet.label}
                        </Typography>
                        <Chip
                          label={snippet.language}
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            bgcolor: 'rgba(13, 53, 89, 0.1)',
                            color: '#0d3559'
                          }}
                        />
                      </Box>
                      <Button
                        size="small"
                        startIcon={
                          copiedIndex === `${tabIndex}-${index}` ? (
                            <CheckIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          )
                        }
                        onClick={() =>
                          handleCopy(snippet.code, `${tabIndex}-${index}`)
                        }
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          color:
                            copiedIndex === `${tabIndex}-${index}`
                              ? '#2e7d32'
                              : '#0d3559',
                          '&:hover': {
                            bgcolor: 'rgba(13, 53, 89, 0.08)'
                          }
                        }}
                      >
                        {copiedIndex === `${tabIndex}-${index}`
                          ? 'Copied!'
                          : 'Copy'}
                      </Button>
                    </Box>
                    <Box
                      sx={{
                        bgcolor: '#F8F9FA',
                        borderRadius: '12px',
                        p: 2,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        lineHeight: 1.6,
                        color: '#1F2937',
                        border: '2px solid #0d3559'
                      }}
                    >
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {snippet.code}
                      </pre>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid #F1F3F9',
              bgcolor: '#F8F9FE',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              💡 Tip: Click "Copy" to copy product code snippets to clipboard
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                bgcolor: '#0d3559',
                borderRadius: '10px',
                fontWeight: 700,
                textTransform: 'none',
                px: 3,
                '&:hover': { bgcolor: '#0a2a47' }
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ProductCodeSnippetHelper;
